import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { enableNewReviewSaveModal } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Alert } from '@launchpad-ui/components';

import { CollapsibleInstructionList } from '#components/InstructionList/CollapsibleInstructionList';
import type { Flag } from '#utils/flags/records/Flag';
import { ConflictKind } from '#utils/instructions/shared/constants';
import type { InstructionIndexToConflictsInfo, SemanticInstruction } from '#utils/instructions/shared/types';

import { filterInstructionsForConflict } from './instructionListUtils';

import styles from './styles.module.css';

export type InstructionListConflictsProps = {
  instructions: SemanticInstruction[];
  flag: Flag;
  projKey: string;
  envKey: string;
  instructionIndexToConflictsInfo: InstructionIndexToConflictsInfo;
  expandTargetList?: boolean;
  onExit?: () => void;
};

export const InstructionListConflicts = ({
  instructions,
  flag,
  projKey,
  envKey,
  instructionIndexToConflictsInfo,
  expandTargetList,
  onExit,
}: InstructionListConflictsProps) => (
  <InstructionListConflictWrapper
    instructions={instructions}
    instructionIndexToConflictsInfo={instructionIndexToConflictsInfo}
  >
    <InstructionListConflictContent
      flag={flag}
      projKey={projKey}
      envKey={envKey}
      expandTargetList={expandTargetList}
      onExit={onExit}
    />
  </InstructionListConflictWrapper>
);

export const InstructionListConflictContent = ({
  flag,
  projKey,
  envKey,
  expandTargetList,
  onExit,
}: Omit<InstructionListConflictsProps, 'instructions' | 'instructionIndexToConflictsInfo'>) => {
  const { conflictKind, filteredInstructions } = useInstructionListConflictsContext();

  return (
    <CollapsibleInstructionList
      instructions={filteredInstructions}
      flag={flag}
      projKey={projKey}
      envKey={envKey}
      expandTargetList={expandTargetList}
      shouldShowConflictLinks={conflictKind !== ConflictKind.PROPOSED_APPROVED_CHANGES_WILL_FAIL}
      onExit={onExit}
    />
  );
};

export const InstructionListConflictWrapper = ({
  children,
  instructions,
  instructionIndexToConflictsInfo,
}: {
  children: ReactNode;
  instructions: SemanticInstruction[];
  instructionIndexToConflictsInfo: InstructionIndexToConflictsInfo;
}) => {
  const conflictKindToHeaderText = enableNewReviewSaveModal()
    ? {
        [ConflictKind.PENDING_CHANGES_WILL_FAIL]:
          'This change conflicts with a pending change and will cause it to fail.',
        [ConflictKind.PROPOSED_SCHEDULED_CHANGES_WILL_FAIL]:
          'This change conflicts with a scheduled change and will cause it to fail.',
        [ConflictKind.PROPOSED_APPROVED_CHANGES_WILL_FAIL]:
          'This change conflicts with an existing approval and will cause it to fail.',
      }
    : {
        [ConflictKind.PENDING_CHANGES_WILL_FAIL]: 'These instructions will cause a pending change to fail.',
        [ConflictKind.PROPOSED_SCHEDULED_CHANGES_WILL_FAIL]:
          'These instructions will fail due to a conflict with a pending change.',
        [ConflictKind.PROPOSED_APPROVED_CHANGES_WILL_FAIL]:
          'These instructions will fail and prevent the change request from being applied',
      };

  return (
    <Alert status="error" className={styles.Alert}>
      {Object.keys(conflictKindToHeaderText).map((conflictKind, i) => {
        const filteredInstructions = filterInstructionsForConflict({
          instructions,
          instructionIndexToConflictsInfo,
          conflictKind: conflictKind as ConflictKind,
        });
        const hasConflictsForKind = filteredInstructions.some((ins) => ins !== undefined);

        if (!hasConflictsForKind) {
          return null;
        }

        if (enableNewReviewSaveModal()) {
          const conflictReasons = new Set<string>();
          instructions.forEach((_, index) => {
            const conflictInfo = instructionIndexToConflictsInfo[index];
            if (conflictInfo?.summaryConflictKind === conflictKind) {
              conflictInfo.conflicts.forEach((conflict) => {
                if (conflict.conflictReason && conflict.conflictKind === conflictKind) {
                  conflictReasons.add(conflict.conflictReason);
                }
              });
            }
          });

          const reasons = [...conflictReasons];

          return (
            <div key={i}>
              <h4 className="u-mb-s u-fw-regular">{conflictKindToHeaderText[conflictKind as ConflictKind]}</h4>
              {reasons.length > 0 && (
                <ul className="u-mb-s">
                  {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        }

        return hasConflictsForKind ? (
          <div key={i}>
            <h4 className="u-mb-s u-fw-regular">{conflictKindToHeaderText[conflictKind as ConflictKind]}</h4>
            <InstructionListConflictsContext.Provider value={{ conflictKind, filteredInstructions }}>
              {children}
            </InstructionListConflictsContext.Provider>
          </div>
        ) : null;
      })}
    </Alert>
  );
};

type InstructionListConflictsContextType = {
  conflictKind: string;
  filteredInstructions: Array<SemanticInstruction | undefined>;
};

export const InstructionListConflictsContext = createContext<InstructionListConflictsContextType | undefined>(
  undefined,
);

export function useInstructionListConflictsContext() {
  const context = useContext(InstructionListConflictsContext);
  if (!context) {
    throw new Error('useInstructionListContext must be used within an InstructionListContextProvider');
  }
  return context;
}
